import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Snackbar, Stack, useMediaQuery,
  Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, LinearProgress, Grid, IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Refresh, Person, LocationOn, Assessment, TrendingUp, ArrowBack, Visibility } from '@mui/icons-material';
import { getTflMonitoring, getTflDetail } from '../API/tflMonitoring';

const statusColor = (s) => {
  if (s === 'Selesai') return 'success';
  if (s === 'Dalam Proses') return 'warning';
  return 'info';
};

const TflMonitoring = () => {
  const navigate = useNavigate();
  const userRole = useSelector((s) => s.authAdmin?.user?.role);
  const isMobile = useMediaQuery('(max-width:800px)');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });
  const [summary, setSummary] = useState(null);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTfl, setDetailTfl] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTflMonitoring();
      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, msg: 'Gagal memuat data', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewDetail = async (tflId, tflName) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailTfl({ id: tflId, username: tflName });
    try {
      const res = await getTflDetail(tflId);
      setDetailData(res.data);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, msg: 'Gagal memuat detail TFL', sev: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Monitoring TFL
          </Typography>
          <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchData}>Muat Ulang</Button>
        </Box>

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Person color="primary" />
                <Typography variant="h5" fontWeight={700}>{summary.totalTFL}</Typography>
                <Typography variant="caption" color="text.secondary">Total TFL</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Assessment color="info" />
                <Typography variant="h5" fontWeight={700}>{summary.totalBantuan}</Typography>
                <Typography variant="caption" color="text.secondary">Total Bantuan</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <LocationOn color="success" />
                <Typography variant="h5" fontWeight={700}>{summary.totalKecamatanCovered}</Typography>
                <Typography variant="caption" color="text.secondary">Kecamatan Cover</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp color="warning" />
                <Typography variant="h5" fontWeight={700}>{summary.avgProgressAll}%</Typography>
                <Typography variant="caption" color="text.secondary">Avg Progress</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>
        )}

        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>TFL</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>No. WA</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Bantuan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Kecamatan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Avg Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: '#888' }}>Tidak ada data TFL.</TableCell></TableRow>
                  ) : rows.map((row, i) => (
                    <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.username}</TableCell>
                      <TableCell>{row.NoWa || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.totalBantuan} color="primary" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {row.kecamatanList.map((k, ki) => (
                            <Chip key={ki} size="small" label={k} variant="outlined" sx={{ fontSize: 10 }} />
                          ))}
                          {row.kecamatanList.length === 0 && <Typography variant="caption">-</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {row.statusCount['Usulan Diterima'] > 0 && <Chip size="small" label={`U: ${row.statusCount['Usulan Diterima']}`} color="info" sx={{ fontSize: 9 }} />}
                          {row.statusCount['Dalam Proses'] > 0 && <Chip size="small" label={`P: ${row.statusCount['Dalam Proses']}`} color="warning" sx={{ fontSize: 9 }} />}
                          {row.statusCount['Selesai'] > 0 && <Chip size="small" label={`S: ${row.statusCount['Selesai']}`} color="success" sx={{ fontSize: 9 }} />}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <Typography variant="caption" fontWeight={700} color="primary">{row.avgProgress}%</Typography>
                        <LinearProgress variant="determinate" value={row.avgProgress} sx={{ height: 5, borderRadius: 2 }} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" title="Lihat Detail" onClick={() => handleViewDetail(row.id, row.username)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth scroll="paper">
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Detail TFL: {detailTfl?.username}</Typography>
              <Button size="small" onClick={() => setDetailOpen(false)}>Tutup</Button>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {detailLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : detailData ? (
              <Box>
                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}><Paper sx={{ p: 1.5, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Total Bantuan</Typography><Typography variant="h6" fontWeight={700}>{detailData.stats.totalBantuan}</Typography></Paper></Grid>
                  <Grid item xs={6} sm={3}><Paper sx={{ p: 1.5, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Kecamatan</Typography><Typography variant="h6" fontWeight={700}>{detailData.stats.kecamatanCount}</Typography></Paper></Grid>
                  <Grid item xs={6} sm={3}><Paper sx={{ p: 1.5, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Selesai</Typography><Typography variant="h6" fontWeight={700} color="success.main">{detailData.stats.statusCount['Selesai']}</Typography></Paper></Grid>
                  <Grid item xs={6} sm={3}><Paper sx={{ p: 1.5, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Avg Progress</Typography><Typography variant="h6" fontWeight={700} color="primary.main">{detailData.stats.avgProgress}%</Typography></Paper></Grid>
                </Grid>

                {/* Kecamatan covered */}
                {detailData.stats.kecamatanList.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">Kecamatan yang ditangani:</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {detailData.stats.kecamatanList.map((k, i) => <Chip key={i} size="small" label={k} color="primary" variant="outlined" />)}
                    </Stack>
                  </Box>
                )}

                {/* Bantuan list */}
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Penerima</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Kecamatan</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Jenis</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Progress ADM</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Progress Keuangan</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Progress Fisik</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Progress</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailData.data.map((b, i) => (
                        <TableRow key={b.id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{b.nama}</TableCell>
                          <TableCell>{b.kecamatan}</TableCell>
                          <TableCell>{b.JenisBantuan}</TableCell>
                          <TableCell sx={{ minWidth: 90 }}>
                            <Typography variant="caption" fontWeight={700} color="info">{b.progressAdm || b.progress}%</Typography>
                            <LinearProgress variant="determinate" value={b.progressAdm || b.progress} sx={{ height: 4, borderRadius: 2 }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 90 }}>
                            <Typography variant="caption" fontWeight={700} color="warning">{b.progressKeu || 0}%</Typography>
                            <LinearProgress variant="determinate" value={b.progressKeu || 0} sx={{ height: 4, borderRadius: 2 }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 90 }}>
                            <Typography variant="caption" fontWeight={700} color="success">{b.progressFisik || 0}%</Typography>
                            <LinearProgress variant="determinate" value={b.progressFisik || 0} sx={{ height: 4, borderRadius: 2 }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <Typography variant="caption" fontWeight={700}>{b.progress}%</Typography>
                            <LinearProgress variant="determinate" value={b.progress} sx={{ height: 4, borderRadius: 2 }} />
                          </TableCell>
                          <TableCell><Chip size="small" color={statusColor(b.statusProses)} label={b.statusProses} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : <Alert severity="warning">Data tidak ditemukan.</Alert>}
          </DialogContent>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.sev} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default TflMonitoring;